import type { Comparaison, GroupCode, SectorCode, TaskLevel } from "@riseafrica/scoring";
import { campagneCourante } from "./campagneCourante";
import { contexteMetaPaiement } from "./meta";
import { CONFIG } from "../config";
import { SID } from "./track";

export interface PackTask {
  texte: string;
  niveau: TaskLevel;
}

export interface PackAction {
  titre: string;
  texte: string;
}

/** Réponse de POST /diagnostic (docs/API.md). Le score n'en fait pas partie. */
export type SourcePack = "referentiel" | "cache" | "modele" | "budget";

export interface DiagnosticResponse {
  metier: string;
  groupe: GroupCode;
  taches: PackTask[];
  actions: PackAction[];
  degraded: boolean;
  cacheHit: boolean;
  /** D'où vient le pack : référentiel relu, cache, modèle, ou repli budget. */
  source: SourcePack;
  /** Volet 3 réécrit pour ce métier, quand le modèle a su le produire. */
  situations?: Array<{ axe: string; q: string; o: Array<[string, number]> }>;
  promptVersion: string;
  referentielVersion: string;
}

/** Le serveur a reconnu que le métier n'était pas interprétable. */
export class MetierInconnuError extends Error {
  constructor() {
    super("metier_inconnu");
  }
}

/** Trop de demandes depuis cette adresse sur l'heure en cours. */
export class TropDeDemandesError extends Error {
  constructor() {
    super("trop_de_demandes");
  }
}

/**
 * Le second diagnostic n'est pas encore ouvert pour cette session.
 *
 * Elle **porte le résultat précédent** : ce n'est pas une erreur à afficher,
 * c'est de quoi réafficher ce que la personne avait mesuré, avec la date de
 * réouverture. L'écran d'accueil l'évite normalement — mais un rechargement au
 * mauvais moment, ou un serveur qui a changé d'avis entre deux appels, passe
 * par ici, et il ne doit pas y trouver un message d'échec.
 */
export class SecondDiagnosticFermeError extends Error {
  constructor(readonly etat: EtatSecondDiagnostic) {
    super("second_diagnostic_ferme");
  }
}

interface DiagnosticRequest {
  mode: "metier" | "cv";
  metier?: string;
  cv?: string;
}

/**
 * Identifiant du parrain, lu à l'arrivée sur le site et conservé pour toute la
 * visite. Transmis avec le diagnostic : sans lui, un parrainage effectif ne peut
 * pas être rattaché à celui qui l'a provoqué (ticket T15).
 */
let parrain: string | null = null;

/** La campagne de la session, jointe aux appels qui comptent — 06/09/2026. */
function avecMeta(): { meta?: ReturnType<typeof contexteMetaPaiement> } {
  const meta = contexteMetaPaiement();
  return meta ? { meta } : {};
}

function avecCampagne(): { campagne?: string } {
  const campagne = campagneCourante();
  return campagne ? { campagne } : {};
}

export function memoriserParrain(valeur: string | null): void {
  if (valeur !== null) parrain = valeur;
}

/** L'appel n'a pas répondu dans le délai imparti. */
export class DelaiDepasseError extends Error {
  constructor() {
    super("delai_depasse");
  }
}

/**
 * Délai maximal côté navigateur. Le serveur borne déjà chaque appel modèle à
 * 20 s et fait au plus trois tentatives avec attente : ce plafond couvre le
 * pire cas serveur avec de la marge, sans laisser l'utilisateur bloqué sur
 * l'écran d'analyse si le réseau se coupe.
 */
const DELAI_MAX_MS = 75000;

/**
 * Appelle le seul point d'entrée qui parle au modèle. La clé du fournisseur
 * vit côté serveur : elle n'apparaît jamais ici.
 */
export async function fetchDiagnostic(request: DiagnosticRequest): Promise<DiagnosticResponse> {
  const controleur = new AbortController();
  const minuterie = window.setTimeout(() => controleur.abort(), DELAI_MAX_MS);

  let response: Response;
  try {
    response = await fetch(CONFIG.diagnosticEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...request, sid: SID, ...(parrain ? { parrain } : {}), ...avecCampagne() }),
      signal: controleur.signal,
    });
  } catch (error) {
    if (controleur.signal.aborted) throw new DelaiDepasseError();
    throw error;
  } finally {
    window.clearTimeout(minuterie);
  }

  if (response.status === 422) throw new MetierInconnuError();
  if (response.status === 429) throw new TropDeDemandesError();
  if (response.status === 409) {
    const corps = (await response.json()) as { erreur?: string } & EtatSecondDiagnostic;
    if (corps.erreur === "second_diagnostic_ferme") throw new SecondDiagnosticFermeError(corps);
  }
  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  return (await response.json()) as DiagnosticResponse;
}

/** Le résultat anonyme envoyé à `/results` (docs/API.md). */
export interface ResultatAEnregistrer {
  mode: "metier" | "cv";
  metierNormalise: string;
  groupe: GroupCode;
  score: number;
  niveau: number;
  /** `texte` n'est transmis qu'en mode métier — jamais un libellé tiré d'un CV. */
  taches: Array<{ texte?: string; niveau: TaskLevel; heures: number }>;
  scoringVersion: string;
  promptVersion: string;
  /**
   * Le pack vient-il du mode dégradé ? — 02/09/2026.
   *
   * L'écran le dit déjà à la personne : ce résultat est générique, relancez.
   * Le serveur doit en tenir compte à deux endroits, sans quoi la phrase est
   * fausse — le diagnostic gratuit se trouvait consommé par un résultat qui
   * n'en était pas un, et la médiane publiée par le baromètre était calculée en
   * partie sur des tâches que le modèle n'a jamais vues.
   */
  degrade: boolean;
}

/**
 * Enregistre le résultat anonyme et récupère la comparaison réelle.
 *
 * Aucun nom, aucun numéro, aucun fragment de CV : le serveur refuserait de les
 * stocker, mais on ne les envoie pas non plus. Un échec est silencieux — perdre
 * une ligne de statistique ne justifie pas de gâcher un résultat à l'écran.
 */
export async function enregistrerResultat(
  resultat: ResultatAEnregistrer,
): Promise<Comparaison | null> {
  try {
    const response = await fetch(CONFIG.resultsEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...resultat, sid: SID, ...(parrain ? { parrain } : {}) }),
    });
    if (!response.ok) return null;
    const corps = (await response.json()) as { comparaison?: Comparaison };
    return corps.comparaison ?? null;
  } catch {
    return null;
  }
}

/** Profil transmis à l'ouverture du paiement, pour la rédaction du rapport. */
export interface ProfilPourRapport {
  metier: string;
  metierNormalise: string;
  groupe: GroupCode;
  taches: Array<{ texte: string; niveau: TaskLevel; heures: number }>;
  valeurAnnuelle: number | null;
  maitrise: number | null;
  /**
   * La personne travaille à son compte. Un booléen, rien de nominatif — et il
   * ne change que la section 4 du rapport, qui parlait de viser un poste à des
   * gens qui n'en cherchent pas.
   */
  aSonCompte: boolean;
  /**
   * Heures de gestion de l'activité, dans l'ordre de `GESTION_TASKS` (T38).
   * Des nombres, rien de nominatif ; `null` pour un salarié.
   */
  gestion?: number[] | null;
}

/**
 * Profil de l'axe dirigeant, transmis à l'ouverture du paiement du plan
 * `entreprise` pour la rédaction du rapport. **Aucun champ d'argent** : le
 * salaire moyen ne quitte pas l'appareil — c'est une promesse écrite sous le
 * champ de saisie, et ce type est ce qui la rend structurelle.
 */
export interface ProfilEntreprisePourRapport {
  secteur: SectorCode;
  /** Tranche d'effectif (0 à 3), telle que choisie à l'écran. */
  staff: number;
  description: string | null;
  postes: Array<{ name: string; hoursPerWeek: number; auto: number }>;
  /**
   * Le coût horaire chargé, **et seulement si le dirigeant l'a autorisé** —
   * 25/08/2026. `null` partout ailleurs, c'est-à-dire par défaut.
   *
   * Le salaire lui-même ne part jamais : ce champ porte le coût horaire qui en
   * est dérivé, parce que c'est la seule valeur dont le calcul a besoin. La
   * distinction est mince — un coût horaire chargé reste proche d'un salaire —
   * et c'est pour cela que la case le dit en toutes lettres au lieu de laisser
   * croire qu'on transmet moins qu'on ne transmet.
   */
  coutHoraire: number | null;
  /** Les dix titres montrés à l'écran, pour que le rapport les tienne — 06/09/2026. */
  titres?: string[] | null;
  /**
   * Les réponses au questionnaire de maturité — 26/08/2026.
   *
   * Des booléens sur des clés déclarées par l'échelle de `@riseafrica/scoring`,
   * et rien d'autre. Le serveur refiltre à la réception : ce qui part d'ici
   * vient d'une réponse que nous avons produite, mais rien ne le prouve
   * là-bas.
   */
  maturite: Record<string, boolean>;
  /**
   * Les rangs des postes où une IA sert déjà — hors échelle, aucun palier.
   *
   * Des rangs et non des libellés : ceux-ci voyagent déjà dans `postes`, et
   * les envoyer deux fois ouvrirait un second endroit où ils divergent.
   */
  postesAvecIa: number[];
  /** `null` quand la question n'a pas été répondue — jamais supposé « non ». */
  donneesClients: boolean | null;
}

export interface OuverturePaiement {
  txId: string;
  montant: number;
  urlPaiement: string;
  /** Vrai quand l'opérateur est simulé : aucun débit n'a lieu. */
  simule?: boolean;
}

/**
 * Ouvre un paiement. Le serveur recalcule le montant : celui affiché ici n'est
 * qu'une information, il n'est pas envoyé comme instruction.
 */
export async function ouvrirPaiement(params: {
  op: string;
  plan: "report" | "bundle" | "entreprise";
  msisdn: string;
  consent: boolean;
  profil: ProfilPourRapport | null;
  profilEntreprise?: ProfilEntreprisePourRapport | null;
  /**
   * Identité de l'acheteur — exigée par certains opérateurs (Chariow émet le
   * reçu). Transmise à l'opérateur, jamais conservée par le serveur. Le
   * serveur répond `acheteur_requis` quand elle manque là où il la faut, et
   * la fenêtre de paiement affiche alors les champs.
   */
  acheteur?: { email: string; prenom: string; nom: string } | null;
  /**
   * Code d'accès du rodage fermé. Quand l'encaissement réel est verrouillé
   * (`PAY_ACCES_CODE` côté serveur), `/pay/init` répond `code_acces_requis`
   * sans lui — la fenêtre de paiement révèle alors le champ.
   */
  codeAcces?: string | null;
  /**
   * L'acheteur accepte-t-il de recevoir le lien de son rapport par courriel ?
   *
   * Coché par défaut à l'écran : c'est la livraison de ce qui a été payé, pas
   * de la prospection. `false` explicite quand il décoche — le serveur n'écrit
   * alors aucune adresse, et le rapport reste sur la page.
   */
  courriel?: boolean;
  /**
   * Le rendez-vous des quatre-vingt-dix jours, par courriel.
   *
   * Distinct de `courriel`, qui livre le document. Décoché par défaut côté
   * écran : on ne recueille pas un consentement en espérant que personne ne
   * décoche.
   */
  rappel?: boolean;
  /**
   * Le point d'étape à trente jours — 28/09/2026. Un second message, donc un
   * second consentement : il ne vaut que si `rappel` est coché, et il reste
   * décoché par défaut.
   */
  etape30?: boolean;
}): Promise<OuverturePaiement> {
  const response = await fetch(CONFIG.paymentEndpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // Le contexte Meta n'est joint qu'avec le consentement de la personne (T43) :
    // c'est lui qui permet au serveur de signaler l'achat confirmé.
    body: JSON.stringify({ ...params, sid: SID, ...avecCampagne(), ...avecMeta() }),
  });
  if (!response.ok) {
    // Le code d'erreur du serveur est porté par le message : c'est ce qui
    // permet à l'écran de distinguer une demande actionnable d'une panne.
    const corps = (await response.json().catch(() => ({}))) as { erreur?: string };
    throw new Error(corps.erreur ?? `HTTP ${response.status}`);
  }
  return (await response.json()) as OuverturePaiement;
}

/**
 * Déclenche l'issue d'un paiement simulé. Le serveur fabrique une notification
 * signée et la fait passer par le webhook réel — signature, idempotence,
 * contrôle de montant et livraison compris.
 *
 * Répond 404 hors environnement de test : ce point d'entrée n'existe pas en
 * production.
 */
export async function simulerPaiement(
  txId: string,
  issue: "reussi" | "echoue" | "annule",
  code: string,
): Promise<boolean> {
  try {
    const response = await fetch(CONFIG.paySimulerEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ txId, issue, code }),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Demande de reprise d'une rédaction échouée.
 *
 * L'écran promettait une relance automatique qui n'existait pas : rien ne
 * rappelait la livraison après un échec, et l'acheteur restait devant un
 * message d'attente indéfinie. La relance est maintenant un geste qu'il peut
 * faire, et qui aboutit ou dit pourquoi il n'aboutira plus.
 */
export async function relancerRapport(
  txId: string,
): Promise<{
  statut: string;
  urlRapport?: string;
  message?: string;
  epuise?: boolean;
  gainHebdo?: number;
  /**
   * Où en est la rédaction, pendant l'attente.
   *
   * « rédaction », « pdf » ou « rangement ». Le serveur l'écrivait depuis le
   * début sans le dire à personne : l'acheteur regardait une phrase fixe
   * pendant jusqu'à neuf minutes.
   */
  etape?: string;
} | null> {
  try {
    const response = await fetch(CONFIG.payRelancerEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ txId }),
    });
    if (!response.ok) return null;
    return (await response.json()) as {
      statut: string;
      urlRapport?: string;
      gainHebdo?: number;
      etape?: string;
      message?: string;
      epuise?: boolean;
    };
  } catch {
    return null;
  }
}

export type StatutPaiement =
  | "en_attente"
  | "paye"
  | "livre"
  | "echec_livraison"
  | "echoue"
  | "annule"
  /*
    Le septième statut, écrit par l'exploitation depuis toujours et déclaré
    d'aucun côté — 02/09/2026.

    Le serveur lui-même ne l'avait pas dans son union : son message tombait
    donc dans le défaut, « le paiement n'a pas abouti, aucun débit n'a eu
    lieu », dit à quelqu'un qui avait payé.
  */
  | "rembourse"
  /** Encaissé au mauvais montant : le débit est reconnu, le rapport suspendu. */
  | "montant_incoherent";

export interface EtatPaiement {
  statut: StatutPaiement;
  message: string;
  /** Présente une fois le rapport livré. Le seul chemin sans WhatsApp. */
  urlRapport?: string;
  /**
   * Le rapport est livré, mais son adresse a été fermée depuis l'exploitation.
   *
   * Sans ce drapeau, l'écran de récupération tombait dans sa branche « en
   * cours » et affichait « Votre rapport est prêt. » sans aucun bouton : une
   * réussite annoncée qui ne mène nulle part.
   */
  adresseFermee?: boolean;
  /** Date du remboursement consigné, sur sa propre transaction. */
  rembourseLe?: string;
  /**
   * Heures que le rapport annonce rendre par semaine, une fois livré.
   *
   * Le total des dix actions, tel qu'il est imprimé sur la couverture du
   * document. Il n'existe pas avant la rédaction, et il n'est rendu qu'à qui
   * détient l'identifiant de sa propre transaction.
   */
  gainHebdo?: number;
  /**
   * Rapport de progression seulement — 28/09/2026. Les heures rendues par
   * semaine, **déclarées**, et les jours écoulés depuis la ligne de base.
   */
  heuresRendues?: number;
  joursProgression?: number;
  /**
   * Où en est la rédaction, pendant l'attente.
   *
   * « rédaction », « pdf » ou « rangement ». Le serveur l'écrivait depuis le
   * premier jour sans la rendre à personne : l'acheteur regardait une phrase
   * fixe pendant jusqu'à neuf minutes.
   */
  etape?: string;
}

/**
 * Consulte l'état d'un paiement. **Ne délivre rien** : la délivrance est
 * déclenchée par le webhook opérateur, côté serveur. Cet appel ne fait
 * qu'informer, et c'est la seule source admissible pour dire « confirmé ».
 */
export async function lireStatutPaiement(txId: string): Promise<EtatPaiement | null> {
  try {
    const response = await fetch(
      `${CONFIG.payStatusEndpoint}?txId=${encodeURIComponent(txId)}`,
    );
    if (!response.ok) return null;
    return (await response.json()) as EtatPaiement;
  } catch {
    return null;
  }
}

/** État du parrainage : filleuls ayant réellement complété un diagnostic. */
/** Ce qu'une session a déjà mesuré, et quand elle pourra recommencer. */
export interface EtatSecondDiagnostic {
  ouvert: boolean;
  motif: "jamais_mesure" | "delai_ecoule" | "parrainage" | "delai_en_cours";
  dernierLe: string | null;
  rouvreLe: string | null;
  precedent: {
    metierNormalise: string;
    groupe: string;
    score: number;
    niveau: number;
    mode: "metier" | "cv";
    scoringVersion: string;
  } | null;
}

export interface EtatParrainage {
  filleuls: number;
  seuil: number;
  ouvert: boolean;
  /**
   * L'état du second diagnostic, servi par le même appel — 31/08/2026.
   *
   * Facultatif : une réponse d'un serveur antérieur à ce jour ne le porte pas,
   * et l'écran doit alors se comporter comme avant plutôt que de fermer. Un
   * champ absent n'est pas un refus.
   */
  second?: EtatSecondDiagnostic;
}

/**
 * Lit l'état du parrainage côté serveur.
 *
 * C'est la seule source admissible pour la phrase « deux personnes ont passé
 * le test depuis votre lien » : le nombre de partages émis, lui, ne dit que ce
 * que l'utilisateur a fait, pas ce qu'il a provoqué.
 */
export async function lireParrainage(): Promise<EtatParrainage | null> {
  try {
    const response = await fetch(
      `${CONFIG.referralEndpoint}?sid=${encodeURIComponent(SID)}`,
    );
    if (!response.ok) return null;
    return (await response.json()) as EtatParrainage;
  } catch {
    return null;
  }
}

/**
 * L'action telle que le serveur la sert : une part offerte, le reste retenu.
 *
 * Le prompt, le détail des étapes suivantes et la vérification **ne sont pas
 * dans cette réponse**. Ce n'est pas un masquage d'affichage : les masquer en
 * CSS les aurait laissés lisibles dans les outils de développement.
 */
export interface ActionPhare {
  titre: string;
  pourquoi: string;
  /** Toutes les étapes en titres ; seule la première porte son détail. */
  etapes: Array<{ titre: string; detail?: string }>;
  piege: string;
  effort: string;
  gainHeures: number;
  retenu: { etapes: number; prompt: boolean; verification: boolean };
}

export interface PlanActions {
  action: ActionPhare;
  /** Les neuf titres réels. Le contenu, lui, est dans le rapport. */
  verrouillees: string[];
}

/** Pourquoi le plan n'est pas arrivé. Sert à le dire, et à le compter. */
export type EchecPlan = "reseau" | "trop_de_demandes" | "budget" | "modele" | "inattendu";

export type ResultatPlan =
  | { statut: "ok"; plan: PlanActions }
  | { statut: "echec"; raison: EchecPlan };

/**
 * Demande l'action offerte et les neuf titres verrouillés.
 *
 * Appelée **après** l'affichage du score, jamais pendant : le diagnostic occupe
 * déjà une vingtaine des soixante secondes qu'accorde la réécriture hosting.
 *
 * Elle rend une **raison** en cas d'échec, et non plus un simple `null`. La
 * différence n'est pas cosmétique : un plan absent faisait retomber l'écran sur
 * l'affichage précédent sans un mot, si bien qu'un plafond de débit atteint et
 * un modèle en panne se ressemblaient — pour l'utilisateur comme pour nous.
 *
 * Il n'existe toujours **pas de repli générique** : une action inventée
 * contredirait exactement ce qu'elle est censée prouver.
 */
export async function fetchPlanActions(
  metier: string,
  taches: PackTask[],
): Promise<ResultatPlan> {
  let response: Response;
  try {
    response = await fetch(CONFIG.actionEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        metier,
        taches: taches.map((t) => ({ texte: t.texte, niveau: t.niveau })),
        sid: SID,
      }),
    });
  } catch {
    return { statut: "echec", raison: "reseau" };
  }

  if (response.status === 429) return { statut: "echec", raison: "trop_de_demandes" };

  try {
    const corps = (await response.json()) as Partial<PlanActions> & {
      disponible?: boolean;
      raison?: string;
    };
    if (corps.disponible === true && corps.action && Array.isArray(corps.verrouillees)) {
      return { statut: "ok", plan: { action: corps.action, verrouillees: corps.verrouillees } };
    }
    if (corps.raison === "budget") return { statut: "echec", raison: "budget" };
    if (corps.raison === "modele") return { statut: "echec", raison: "modele" };
    return { statut: "echec", raison: "inattendu" };
  } catch {
    return { statut: "echec", raison: "inattendu" };
  }
}


/** Un poste de travail proposé au dirigeant. */
export interface PosteEntreprise {
  name: string;
  short: string;
  auto: number;
}

export interface ReponsePostes {
  postes: PosteEntreprise[];
  /** Vrai quand les postes viennent de la banque et non du modèle. */
  degraded: boolean;
  cacheHit: boolean;
}

/**
 * Les sept postes à mesurer chez une PME.
 *
 * Contrairement au plan d'actions, **cet appel ne rend jamais d'échec** : le
 * serveur sert la banque du secteur quand le modèle n'aboutit pas. Sans postes
 * il n'y aurait pas de questionnaire, donc pas de montant, donc pas de résultat
 * du tout. En cas de panne réseau, le front n'a pas de banque sous la main : il
 * rend `null`, et l'écran propose de réessayer.
 */
export async function fetchPostesEntreprise(
  secteur: string,
  description: string,
): Promise<ReponsePostes | null> {
  try {
    const response = await fetch(CONFIG.entrepriseEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ secteur, description, sid: SID }),
    });
    const corps = (await response.json()) as Partial<ReponsePostes>;
    if (!Array.isArray(corps.postes) || corps.postes.length === 0) return null;
    return {
      postes: corps.postes,
      degraded: corps.degraded === true,
      cacheHit: corps.cacheHit === true,
    };
  } catch {
    return null;
  }
}


/** Ce que déclarent les autres entreprises d'un secteur, au-delà du seuil. */
export interface RepereSecteur {
  disponible: boolean;
  seuil: number;
  effectif: number;
  medianHeures?: number;
  partAutomatisable?: number;
}

/**
 * Le repère du secteur, ou `null`.
 *
 * `null` couvre autant la panne réseau que le secteur qui n'a pas encore
 * atteint le seuil : dans les deux cas l'écran n'affiche rien. Un repère est
 * un supplément — le faire échouer bruyamment le rendrait plus visible absent
 * que présent.
 */
export async function fetchRepereSecteur(secteur: string): Promise<RepereSecteur | null> {
  try {
    const response = await fetch(
      `${CONFIG.repereEndpoint}?secteur=${encodeURIComponent(secteur)}`,
    );
    if (!response.ok) return null;
    const corps = (await response.json()) as RepereSecteur;
    return corps.disponible === true ? corps : null;
  } catch {
    return null;
  }
}

/**
 * L'action offerte du dirigeant et les neuf titres verrouillés.
 *
 * Même contrat que `fetchPlanActions`, et mêmes raisons : pas de repli
 * générique — le montant affiché au-dessus ne dépend pas de cette rédaction,
 * et une action inventée contredirait ce qu'elle prouve. L'écran sait dire
 * l'échec, et le rattraper.
 */
export async function fetchPlanEntreprise(
  secteur: string,
  description: string,
  postes: Array<{ name: string; hoursPerWeek: number }>,
): Promise<ResultatPlan> {
  let response: Response;
  try {
    response = await fetch(CONFIG.entreprisePlanEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ secteur, description, postes, sid: SID }),
    });
  } catch {
    return { statut: "echec", raison: "reseau" };
  }

  if (response.status === 429) return { statut: "echec", raison: "trop_de_demandes" };

  try {
    const corps = (await response.json()) as Partial<PlanActions> & {
      disponible?: boolean;
      raison?: string;
    };
    if (corps.disponible === true && corps.action && Array.isArray(corps.verrouillees)) {
      return { statut: "ok", plan: { action: corps.action, verrouillees: corps.verrouillees } };
    }
    if (corps.raison === "budget") return { statut: "echec", raison: "budget" };
    if (corps.raison === "modele") return { statut: "echec", raison: "modele" };
    return { statut: "echec", raison: "inattendu" };
  } catch {
    return { statut: "echec", raison: "inattendu" };
  }
}

/**
 * Les réglages du produit, tels que le serveur les publie.
 *
 * `null` veut dire « pas encore su », jamais « fermé » : un écran ne se retire
 * pas parce qu'une requête est lente. Les appelants doivent distinguer les
 * trois états — inconnu, ouvert, fermé.
 */
export interface ReglagesPublics {
  modeCv: boolean;
  /**
   * La formule à 9 000 F est-elle réellement assurée ?
   *
   * Publiée parce qu'elle décide d'un écran — la seconde carte d'offre. Elle se
   * décidait sur `VITE_ACCOMPAGNEMENT`, figée à la construction : l'ouvrir
   * depuis `/pilotage` n'affichait rien, et le serveur refusait le plan que le
   * navigateur ne proposait de toute façon pas. Deux moitiés justes, aucune
   * jointure.
   */
  accompagnementActif: boolean;
  /**
   * Le suivi de progression est-il allumé ? Il décide d'une case du tunnel de
   * paiement : le point d'étape à trente jours. Faux par défaut.
   */
  progressionActive: boolean;
  /**
   * Le prix du rapport dirigeant, tel que Chariow le facture réellement.
   *
   * Publié parce qu'il décide de ce qui est **écrit à l'écran**. Un site qui
   * annonce 15 000 F quand l'acheteur en paie 25 000 ne serait pas seulement
   * incohérent : ce serait un faux signal sur le seul chiffre qu'il vérifie.
   */
  prixEntreprise: number;
  /**
   * L'adresse à laquelle le public écrit — remboursement, droits sur les
   * données. Publiée parce qu'elle décide de ce qui est écrit à l'écran, et
   * parce que désigner qui relève cette boîte est une décision qui se prend un
   * matin, pas au rythme d'une livraison de code.
   *
   * Vide quand le serveur n'a rien dit : l'appelant garde alors celle du
   * paquet. Jamais d'adresse absente sous une garantie.
   */
  contactCourriel: string;
  /**
   * Combien de jours un numéro fourni est conservé, tel que la purge l'applique.
   *
   * Publié pour que la politique de confidentialité **dise** ce que le serveur
   * fait, au lieu de le recopier à la main. Zéro veut dire « le serveur n'a
   * rien dit, ou la purge saute cette classe » : la page n'annonce alors aucun
   * délai. Annoncer « zéro jour » serait faux dans les deux cas.
   */
  conservationContactsJours: number;
  /**
   * Le tunnel de paiement doit-il demander nom, prénom et courriel ?
   *
   * Publié pour que l'écran affiche les bons champs **du premier coup**. Il les
   * révélait après un refus du serveur — une règle juste, lue trop tard : avec
   * un seul opérateur branché qui les exige toujours, chaque acheteur essuyait
   * un refus garanti à l'instant où il venait de décider de payer.
   */
  identiteAcheteurRequise: boolean;
  /** Un code d'accès est-il demandé ? Le code lui-même n'est jamais publié. */
  codeAccesRequis: boolean;
  /**
   * Meta est-il autorisé par l'exploitation ? (T43). Coupé depuis `/pilotage`,
   * le bandeau ne s'affiche plus et le pixel ne se charge plus. Un `true`
   * explicite, rien d'autre.
   */
  metaActif: boolean;
  /**
   * L'axe dirigeant est-il ouvert ? (09/10/2026). Fermé depuis `/pilotage`, les
   * portes d'entrée disparaissent et `/dirigeant` affiche sa fermeture.
   */
  dirigeantActif: boolean;
  /**
   * Les mentions légales posées depuis `/pilotage`, sous leurs clés serveur.
   *
   * Seules celles qui existent sont envoyées : une mention absente reste
   * absente, et la page affiche son marqueur « en attente » exactement comme
   * quand ces valeurs vivaient en dur dans le paquet construit.
   */
  mentions: Record<string, string>;
}

/** Ne retient que des mentions plausibles : clé attendue, chaîne non vide et bornée. */
function lireMentions(brut: unknown): Record<string, string> {
  if (typeof brut !== "object" || brut === null) return {};
  const propres: Record<string, string> = {};
  for (const [cle, valeur] of Object.entries(brut as Record<string, unknown>)) {
    if (!/^mention[A-Za-z]{1,60}$/.test(cle)) continue;
    if (typeof valeur !== "string") continue;
    const net = valeur.trim();
    if (net === "" || net.length > 200) continue;
    propres[cle] = net;
  }
  return propres;
}

export async function lireReglages(): Promise<ReglagesPublics | null> {
  try {
    const response = await fetch(CONFIG.reglagesEndpoint, { method: "GET" });
    if (!response.ok) return null;
    const donnees = (await response.json()) as Partial<ReglagesPublics>;
    return {
      modeCv: donnees.modeCv !== false,
      // Faux par défaut, contrairement au mode CV : proposer une formule que le
      // serveur refusera est pire que ne pas la proposer. Un `true` explicite,
      // rien d'autre.
      accompagnementActif: donnees.accompagnementActif === true,
      // Même posture : un `true` explicite, rien d'autre.
      progressionActive: donnees.progressionActive === true,
      metaActif: donnees.metaActif === true,
      // L'inverse des précédents : un `false` explicite, rien d'autre. Un
      // serveur qui ne connaît pas encore ce réglage n'a rien fermé.
      dirigeantActif: donnees.dirigeantActif !== false,
      // Zéro ou absent veut dire « le serveur n'a rien dit » : l'appelant garde
      // alors le prix du module partagé plutôt que d'afficher un prix nul.
      prixEntreprise:
        typeof donnees.prixEntreprise === "number" && donnees.prixEntreprise > 0
          ? donnees.prixEntreprise
          : 0,
      // Même posture : vide veut dire « le serveur n'a rien dit », et
      // l'appelant garde l'adresse du paquet.
      contactCourriel:
        typeof donnees.contactCourriel === "string" && donnees.contactCourriel.includes("@")
          ? donnees.contactCourriel
          : "",
      // Un délai n'a de sens qu'entier et positif. Tout le reste — absent,
      // négatif, fractionnaire, non numérique — retombe sur zéro, c'est-à-dire
      // sur « la page n'annonce pas de délai ».
      conservationContactsJours:
        typeof donnees.conservationContactsJours === "number" &&
        Number.isInteger(donnees.conservationContactsJours) &&
        donnees.conservationContactsJours > 0
          ? donnees.conservationContactsJours
          : 0,
      /*
        Faux par défaut, des deux côtés.

        Une lecture qui échoue ne doit pas faire apparaître des champs que
        l'opérateur ne réclame pas : le tunnel retombe alors sur l'ancien
        comportement — révéler après refus — qui est dégradé mais jamais
        bloquant.
      */
      identiteAcheteurRequise: donnees.identiteAcheteurRequise === true,
      codeAccesRequis: donnees.codeAccesRequis === true,
      /*
        Relu entrée par entrée plutôt que recopié en bloc.

        Ce qui arrive du réseau est rendu sur une page publique : on ne garde
        que des chaînes non vides, sous des clés qui ressemblent à des clés de
        mention. Une réponse abîmée fait donc réapparaître le marqueur
        « en attente » — l'état d'avant, honnête — plutôt que d'imprimer une
        valeur douteuse sous le nom de l'éditeur.
      */
      mentions: lireMentions(donnees.mentions),
    };
  } catch {
    return null;
  }
}

/*
  Le rapport de progression — 28/09/2026.

  Trois appels, un par geste de la page : relire la ligne de base, s'engager,
  mesurer. Le troisième ne rend que l'aperçu gratuit : le serveur n'envoie
  jamais le détail, et l'écran n'a donc rien à cacher.
*/

export interface TacheDeBase {
  texte: string;
  niveau: TaskLevel;
  heures: number;
}

export interface EngagementPris {
  action: number;
  titre: string;
  tache: string;
  moment: string;
  minutesAvant: number | null;
}

export interface LigneDeBasePublique {
  metier: string;
  groupe: GroupCode;
  aSonCompte: boolean;
  gestion: number[] | null;
  taches: TacheDeBase[];
  actions: Array<{ titre: string; tache: string }>;
  engagements: EngagementPris[];
  creeLe: string;
  rendezVousLe: string;
  jours: number;
  mesureOuverte: boolean;
  joursAvantMesure: number;
  progressionLivree: boolean;
  /** Le point d'étape à trente jours est-il ouvert (de trois semaines à la mesure) ? */
  pointEtapeOuvert: boolean;
  /** La réponse déjà donnée au point d'étape, ou `null`. */
  etape30: ReponseEtape | null;
}

export type ReponseEtape = "commence" | "pas_encore" | "abandonne";

export interface SuiteEtape {
  titre: string;
  texte: string;
  modifier: boolean;
}

export async function noterRecommandation(jeton: string, note: number): Promise<boolean> {
  const r = await posterProgression({ jeton, action: "note", note });
  return r.ok;
}

export async function repondreEtape(
  jeton: string,
  reponse: ReponseEtape,
): Promise<{ suite: SuiteEtape } | { erreur: string }> {
  const r = await posterProgression({ jeton, action: "etape", reponse });
  return r.ok ? (r.donnees as { suite: SuiteEtape }) : { erreur: r.erreur };
}

export interface ApercuProgression {
  scoreAvant: number;
  scoreApres: number;
  ecartScore: number;
}

/** Pourquoi la page ne peut pas s'afficher : fermée, introuvable, ou panne. */
export type RefusProgression = "fermee" | "introuvable" | "indisponible";

export async function lireProgression(
  jeton: string,
): Promise<{ base: LigneDeBasePublique } | { refus: RefusProgression }> {
  try {
    const reponse = await fetch(`${CONFIG.progressionEndpoint}?jeton=${encodeURIComponent(jeton)}`);
    if (reponse.status === 404) {
      const corps = (await reponse.json().catch(() => ({}))) as { erreur?: string };
      return { refus: corps.erreur === "progression_fermee" ? "fermee" : "introuvable" };
    }
    if (!reponse.ok) return { refus: "indisponible" };
    return { base: (await reponse.json()) as LigneDeBasePublique };
  } catch {
    return { refus: "indisponible" };
  }
}

async function posterProgression(corps: Record<string, unknown>): Promise<{ ok: true; donnees: unknown } | { ok: false; erreur: string }> {
  try {
    const reponse = await fetch(CONFIG.progressionEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(corps),
    });
    const donnees = (await reponse.json().catch(() => ({}))) as { erreur?: string };
    if (!reponse.ok) return { ok: false, erreur: donnees.erreur ?? "indisponible" };
    return { ok: true, donnees };
  } catch {
    return { ok: false, erreur: "indisponible" };
  }
}

export async function prendreEngagements(
  jeton: string,
  engagements: Array<{ action: number; moment: string; minutes: number | null }>,
): Promise<{ engagements: EngagementPris[] } | { erreur: string }> {
  const r = await posterProgression({ jeton, action: "engager", engagements });
  return r.ok ? (r.donnees as { engagements: EngagementPris[] }) : { erreur: r.erreur };
}

export async function mesurerProgression(
  jeton: string,
  heures: number[],
  minutes: Array<number | null>,
): Promise<{ apercu: ApercuProgression; jours: number } | { erreur: string }> {
  const r = await posterProgression({ jeton, action: "mesurer", heures, minutes });
  return r.ok ? (r.donnees as { apercu: ApercuProgression; jours: number }) : { erreur: r.erreur };
}
